/**
 * useEvidenceScroll
 *
 * Helper for the Coverage Audit detail page: scrolls to the DOM anchor
 * representing an evidence span (a transcript segment, a summary section,
 * or a note section) and applies a transient amber ring so the auditor's
 * eye lands on the right spot.
 *
 * Anchors are owned by the detail page:
 *   - Transcript segment:  id = `transcript-segment-${order}`
 *   - Summary section:     id = `summary-section-${key}`
 *   - Note section:        id = `note-section-${key}`
 *
 * The hook returns two callbacks:
 *   - `focusEvidence(span)` — scroll to the most-specific anchor available
 *     for an EvidenceSpan (segment if present, else section).
 *   - `focusSection(side, key)` — scroll directly to a section card on a
 *     given column. Used by section-card "N issues" badges that link back
 *     to the first relevant transcript/note row.
 */
import { useCallback } from 'react';
import type { EvidenceSpan } from '../stores/coverageAuditStore';

const FLASH_CLASSES = ['ring-2', 'ring-amber-400', 'ring-offset-2'];
const FLASH_MS = 1800;

function flash(el: HTMLElement) {
  // Remove any prior flash so a rapid second click restarts the animation.
  for (const c of FLASH_CLASSES) el.classList.remove(c);
  // Force reflow so re-adding the classes triggers the transition again.
  void el.offsetWidth;
  for (const c of FLASH_CLASSES) el.classList.add(c);
  window.setTimeout(() => {
    for (const c of FLASH_CLASSES) el.classList.remove(c);
  }, FLASH_MS);
}

function scrollToId(id: string): boolean {
  const el = document.getElementById(id);
  if (!el) {
    // Loud failure — when the auditor clicks an evidence chip and nothing
    // happens, we want to know exactly why. The likely cause is a
    // section-name mismatch between the LLM judge's output and the
    // section keys the FE renders with. The console message lists every
    // candidate id so you can spot the typo immediately.
    const nearby = Array.from(document.querySelectorAll('[id^="transcript-segment-"], [id^="summary-section-"], [id^="note-section-"]'))
      .map((n) => (n as HTMLElement).id);
    console.warn('[evidence-scroll] target not found', { wantedId: id, candidates: nearby });
    return false;
  }
  console.log('[evidence-scroll] scrolling to', id);
  el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  flash(el);
  return true;
}

export function useEvidenceScroll() {
  const focusEvidence = useCallback((span: EvidenceSpan) => {
    console.log('[evidence-scroll] focusEvidence', span);
    // Most specific anchor first: transcript segment by order.
    if (span.source === 'transcript' && typeof span.segmentOrderStart === 'number') {
      if (scrollToId(`transcript-segment-${span.segmentOrderStart}`)) return;
    }
    // Section-level fallback (summary or note via the `chart` source alias).
    if (span.section) {
      // The LLM judge may emit the section in either camelCase
      // (chiefComplaint) or snake_case (chief_complaint) depending on
      // prompt version. Try both shapes before giving up.
      const sectionKey = String(span.section);
      const altKey = sectionKey.includes('_')
        ? sectionKey.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
        : sectionKey.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
      const sides: Array<'summary' | 'note'> =
        span.source === 'summary'
          ? ['summary']
          : span.source === 'chart'
            ? ['note']
            : ['summary', 'note']; // unknown source — try both
      for (const side of sides) {
        if (scrollToId(`${side}-section-${sectionKey}`)) return;
        if (scrollToId(`${side}-section-${altKey}`)) return;
      }
    }
    // Last resort: do nothing — the evidence chip just won't scroll anywhere.
    // Better than throwing; the chip text already shows the location.
  }, []);

  const focusSection = useCallback(
    (side: 'summary' | 'note' | 'transcript', key: string) => {
      if (side === 'transcript') {
        scrollToId(`transcript-segment-${key}`);
        return;
      }
      scrollToId(`${side}-section-${key}`);
    },
    [],
  );

  return { focusEvidence, focusSection };
}
